import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { getGame, DEFAULT_GAME } from "../lib/games";
import Layout from "../components/Layout";
import Seo from "../components/Seo";
import { History, ChevronLeft, ChevronRight } from "lucide-react";

export default function PreviousResult() {
  const { game } = useParams();
  const slug = game || DEFAULT_GAME;
  const g = getGame(slug);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    if (page === 1) {
      try {
        const c = sessionStorage.getItem(`pr_${slug}`);
        if (c) { const d = JSON.parse(c); setItems(d.items || []); setPages(d.pages || 1); setTotal(d.total || 0); setLoading(false); }
        else setLoading(true);
      } catch { setLoading(true); }
    } else setLoading(true);
    api.get(`/results?game=${slug}&page=${page}&limit=20`).then(({ data }) => {
      if (!active) return;
      setItems(data.items || []);
      setPages(data.pages || 1);
      setTotal(data.total || 0);
      setLoading(false);
      if (page === 1) {
        try { sessionStorage.setItem(`pr_${slug}`, JSON.stringify({ items: data.items, pages: data.pages, total: data.total })); } catch {}
      }
    });
    return () => { active = false; };
  }, [page, slug]);

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://nightteerresulttoday.com/" },
      { "@type": "ListItem", position: 2, name: "Previous Result", item: "https://nightteerresulttoday.com/previous-result" },
    ],
  };

  return (
    <Layout>
      <Seo
        title={`${g.name} Previous Result | Past FR SR Results`}
        description={`Browse ${g.name} previous results with Date, FR and SR. Newest results appear first.`}
        path={game ? `/previous-result/${slug}` : "/previous-result"}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <h1 className="font-oswald text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
          <History className="text-red-600" size={36} /> {g.name}
        </h1>
        <p className="mt-3 text-slate-600 max-w-2xl leading-relaxed">
          Previous results for {g.name} showing the Date, First Round (FR) and Second Round (SR). The most recent
          results appear at the top.
        </p>

        <div className="mt-8 overflow-x-auto w-full bg-white rounded-xl border border-slate-200">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-4 sm:px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                <th className="px-4 sm:px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">FR</th>
                <th className="px-4 sm:px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">SR</th>
              </tr>
            </thead>
            <tbody data-testid="previous-results-table">
              {loading ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <tr key={`sk-${i}`} className="border-b border-slate-100 even:bg-slate-50">
                    <td className="px-4 sm:px-6 py-4"><div className="h-4 w-24 rounded bg-slate-200 animate-pulse" /></td>
                    <td className="px-4 sm:px-6 py-4"><div className="mx-auto h-6 w-10 rounded bg-slate-200 animate-pulse" /></td>
                    <td className="px-4 sm:px-6 py-4"><div className="mx-auto h-6 w-10 rounded bg-slate-200 animate-pulse" /></td>
                  </tr>
                ))
              ) : items.length === 0 ? (
                <tr><td colSpan={3} className="px-4 py-8 text-center text-sm text-slate-500">No results published yet.</td></tr>
              ) : (
                items.map((row) => (
                  <tr key={row.id} className="border-b border-slate-100 even:bg-slate-50 hover:bg-slate-100 transition-colors">
                    <td className="px-4 sm:px-6 py-4 text-sm font-medium text-slate-700 whitespace-nowrap">{row.date}</td>
                    <td className="px-4 sm:px-6 py-4 text-center font-oswald text-xl font-bold text-red-600">{row.fr || "--"}</td>
                    <td className="px-4 sm:px-6 py-4 text-center font-oswald text-xl font-bold text-red-600">{row.sr || "--"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-6">
          <span className="text-sm text-slate-500">{total} result{total === 1 ? "" : "s"} total</span>
          {pages > 1 && (
            <div className="flex items-center gap-3">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-slate-300 text-sm font-semibold text-slate-700 disabled:opacity-40 hover:bg-slate-50"
                data-testid="previous-prev-page"
              >
                <ChevronLeft size={16} /> Prev
              </button>
              <span className="text-sm text-slate-500">{page} / {pages}</span>
              <button
                disabled={page >= pages}
                onClick={() => setPage((p) => p + 1)}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-slate-300 text-sm font-semibold text-slate-700 disabled:opacity-40 hover:bg-slate-50"
                data-testid="previous-next-page"
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
